import React, { useCallback, useEffect } from 'react';

import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { VerticalBarChart } from '../../../common/components/Charts/VerticalBarChart';
import {
  LabelValue,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import ValidationAlert from '../../../app/components/ValidationAlert';

import {
  DateRangeFilterApplied,
  SuperAdminDateRangeFilter,
} from './SuperAdminDateRangeFilter';
import {
  DateRangeState,
  initDefaultLastDaysRange,
  toDateEnd,
  toDateStart,
} from './SuperAdminTopCompaniesBase';

export const SuperAdminExamsTab: React.FC = () => {
  const spd = useSpdCore();
  const [dataCloseExams, setDataCloseExams] = React.useState<LabelValue[]>([]);
  const [dataCloseExamsByDays, setDataCloseExamsByDays] = React.useState<
    LabelValue[]
  >([]);
  const [filters, setFilters] = React.useState<DateRangeState>({
    dateFrom: '',
    dateTo: '',
  });
  const [applied, setApplied] = React.useState<DateRangeFilterApplied>({
    dateFrom: '',
    dateTo: '',
  });
  const [errors, setErrors] = React.useState<string[]>([]);

  const [filtersMonthly, setFiltersMonthly] = React.useState<DateRangeState>({
    dateFrom: '',
    dateTo: '',
  });
  const [errorsMonthly, setErrorsMonthly] = React.useState<string[]>([]);

  const loadByDays = useCallback(
    (a: DateRangeFilterApplied) => {
      setErrors([]);
      spd.reports
        .reportCloseExams(
          true,
          toDateStart(a.dateFrom),
          toDateEnd(a.dateTo),
          a.companyId,
        )
        .then(data => setDataCloseExamsByDays(data))
        .catch(async error => {
          setDataCloseExamsByDays([]);
          setDataCloseExams([]);
          console.error('Error during report close exams', error);
          setErrors(await responseErrors(error as ResponseError));
        });
    },
    [spd.reports],
  );

  const loadByMonths = useCallback(
    (a: DateRangeFilterApplied) => {
      setErrorsMonthly([]);
      spd.reports
        .reportCloseExams(
          undefined,
          toDateStart(a.dateFrom),
          toDateEnd(a.dateTo),
          a.companyId,
        )
        .then(data => setDataCloseExams(data))
        .catch(async error => {
          setDataCloseExams([]);
          console.error('Error during report close exams (monthly)', error);
          setErrorsMonthly(await responseErrors(error as ResponseError));
        });
    },
    [spd.reports],
  );

  useEffect(() => {
    void initDefaultLastDaysRange(
      spd.params.getReportLastDays,
      range => {
        setFilters(range);
        setApplied(range);
        loadByDays(range);
      },
      async error => {
        console.error('Error during report last days param fetch', error);
        setErrors(await responseErrors(error as ResponseError));
      },
    );
  }, [spd.params, loadByDays]);

  useEffect(() => {
    loadByMonths({ dateFrom: '', dateTo: '' });
  }, [loadByMonths]);

  return (
    <>
      <SuperAdminDateRangeFilter
        filters={filters}
        setFilters={setFilters}
        withCompany={true}
        onApply={a => {
          setErrors([]);
          setDataCloseExamsByDays([]);
          setApplied(a);
          loadByDays(a);
        }}
      />
      <ValidationAlert
        show={errors.length > 0}
        errors={errors}
        className="mt-3"
      />
      {errors.length === 0 && (
        <VerticalBarChart
          title={`Zestawienie zakończonych badań w zakresie: ${applied.dateFrom} - ${applied.dateTo}`}
          labels={dataCloseExamsByDays.map(o => o.label)}
          data={dataCloseExamsByDays.map(o => o.count)}
          datasetLabel="Liczba zakończonych badań"
          backgroundColor="rgb(18, 72, 135, 0.5)"
          borderColor="rgb(18, 72, 135, 1)"
          key="examsByDays"
        />
      )}
      <SuperAdminDateRangeFilter
        filters={filtersMonthly}
        setFilters={setFiltersMonthly}
        withCompany={true}
        monthPicker={true}
        applyLabel="Pokaż zestawienie miesięczne"
        onApply={a => {
          setErrorsMonthly([]);
          setDataCloseExams([]);
          loadByMonths(a);
        }}
      />
      <ValidationAlert
        show={errorsMonthly.length > 0}
        errors={errorsMonthly}
        className="mt-3"
      />
      {errorsMonthly.length === 0 && (
        <VerticalBarChart
          title="Zestawienie zakończonych badań w podziale na miesiące"
          labels={dataCloseExams.map(o => o.label)}
          data={dataCloseExams.map(o => o.count)}
          datasetLabel="Liczba zakończonych badań"
          backgroundColor="rgba(53, 162, 235, 0.5)"
          borderColor="rgba(53, 162, 235, 1)"
          key="ExamsByMonths"
        />
      )}
    </>
  );
};
